import { useEffect, useRef, useState, type CSSProperties } from "react";
import "./Hero.css";
import { Link } from "react-router-dom";
import { useLocale } from "@/i18n/useLocale";
import BrandMarquee from "@/components/BrandMarquee/BrandMarquee";
import Highlight from "@/components/Highlight/Highlight";
import HeroSlides from "@/components/HeroSlides/HeroSlides";
import { SLIDE_ACCENT, type SlideId } from "@/components/HeroSlides/slides";

/**
 * Hero con un titular unico, una bajada y dos botones: uno principal (hablar)
 * y uno secundario (ver casos). A la derecha esta el escenario con la demo o
 * los proyectos reales de cada linea de negocio -empleados virtuales,
 * e-commerce y software a medida-, que se elige con sus pestañas y, si nadie
 * toca nada, avanza solo.
 *
 * Cada cosa se dice una sola vez. Antes las tres lineas aparecian en el
 * eyebrow, en la bajada, en tres tarjetas y en las pestañas, y el unico boton
 * era uno de contorno: tres tarjetas, un boton y tres pestañas competian y
 * ninguno era "la" accion. Las pestañas ya son la puerta a cada linea.
 *
 * Antes tambien el titular, la bajada y el boton rotaban con el slide: quien
 * buscaba software o e-commerce veia primero "empleados virtuales". Con un
 * titular fijo la primera pantalla le habla a las tres audiencias, y deja de
 * depender de que slide toque al pre-renderizar.
 *
 * El estado del slide vive aca y no en el escenario para que el acento del
 * titular y el escenario cambien siempre juntos.
 */
function Hero() {
  const { t, path } = useLocale();
  const slides = t.hero.slides;

  const [active, setActive] = useState(0);
  const [activations, setActivations] = useState<number[]>(() => slides.map((_, i) => (i === 0 ? 1 : 0)));
  const [autoPlay, setAutoPlay] = useState(true);
  const [held, setHeld] = useState(false);
  const [inView, setInView] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);

  // Fuera de pantalla el slide no tiene por que seguir avanzando.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.2,
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  function select(index: number, manual: boolean) {
    // Elegir a mano un slide (o tocar algo adentro) corta el avance automatico
    // para siempre: WCAG 2.2.2 pide poder frenar el movimiento de mas de 5s.
    if (manual) setAutoPlay(false);
    if (index === active) return;

    setActive(index);
    setActivations((prev) => prev.map((count, i) => (i === index ? count + 1 : count)));
  }

  const slide = slides[active];
  const slideId = slide.id as SlideId;
  const accent = SLIDE_ACCENT[slideId];

  // Los anclas se arman desde la home del idioma actual: en ingles apuntan a
  // /en#empleados y no devuelven al usuario al sitio en español.
  const home = path("home");

  return (
    <section
      className="hero"
      id="home"
      ref={sectionRef}
      data-slide={slideId}
      style={{ "--slide-accent": accent.color, "--slide-hue": accent.hue } as CSSProperties}
    >
      <div className="hero__content">
        <div className="hero__left">
          <h1 className="hero__title">
            <Highlight text={t.hero.umbrella.title} className="hero__accent" />
          </h1>

          <p className="hero__subtitle">{t.hero.umbrella.subtitle}</p>

          <div className="hero__actions">
            <Link to={`${home}#contacto`} className="hero__btn hero__btn--primary">
              {t.hero.cta}
            </Link>
            <Link to={path("cases")} className="hero__btn hero__btn--secondary">
              {t.hero.ctaCases}
            </Link>
          </div>

          {/* La prueba va arriba, no enterrada tres pantallas abajo: los
              nombres son lo que hace creible todo lo demas. */}
          <BrandMarquee />
        </div>

        <div className="hero__right">
          <HeroSlides
            active={active}
            activations={activations}
            autoPlay={autoPlay}
            paused={held || !inView}
            onSelect={select}
            onHoldChange={setHeld}
          />
        </div>
      </div>
    </section>
  );
}

export default Hero;
